import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { ChevronDown } from 'lucide-react';
import { CONSTRAINT_OPTIONS } from '../../lib/kinsey-data';

export const KinseyQuestion = ({ question, questionIndex, totalQuestions, selectedValue, constraintValue, onSelect, onConstraintSelect }) => {
  const showConstraint = question.hasConstraint && selectedValue !== undefined && selectedValue !== null;
  const constraintRef = useRef(null);

  useEffect(() => {
    if (showConstraint && constraintRef.current) {
      setTimeout(() => {
        constraintRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [showConstraint]);

  return (
    <section className="min-h-screen py-24 px-6 pb-40" data-testid="kinsey-question-section">
      <div className="max-w-2xl mx-auto">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-10">
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
              {questionIndex + 1} / {totalQuestions}
            </span>
            <div className="w-full bg-border/40 rounded-full h-1 mt-3">
              <div
                className="bg-[hsl(var(--gold))] h-1 rounded-full transition-all duration-500"
                style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
                data-testid="kinsey-progress-bar"
              />
            </div>
          </div>

          <h2
            className="text-2xl sm:text-3xl font-light text-foreground mb-10 leading-relaxed"
            data-testid="kinsey-question-text"
          >
            {question.text}
          </h2>

          <div className="space-y-3" data-testid="kinsey-options">
            {question.options.map((option, i) => (
              <motion.div
                key={option.label}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.05 + i * 0.03 }}
              >
                <Card
                  className={`p-4 cursor-pointer transition-all border ${
                    selectedValue === option.value
                      ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                      : 'border-border bg-card hover:border-[hsl(var(--teal)/0.3)]'
                  }`}
                  onClick={() => onSelect(option.value)}
                  data-testid={`kinsey-option-${question.id}-${i}`}
                >
                  <span className="text-base font-light text-foreground">{option.label}</span>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Constraint follow-up for behavior questions */}
          {showConstraint && (
            <motion.div
              ref={constraintRef}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-8 pt-6 border-t border-primary/20"
            >
              <div className="flex items-center gap-2 mb-4">
                <ChevronDown className="w-4 h-4 text-primary animate-bounce" />
                <p className="text-sm font-medium text-foreground">One more part to this question</p>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Does this reflect what you actually want?</p>
              <div className="space-y-3" data-testid="constraint-options">
                {CONSTRAINT_OPTIONS.map((option, i) => (
                  <Card
                    key={option.value}
                    className={`p-4 cursor-pointer transition-all border ${
                      constraintValue === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/30'
                    }`}
                    onClick={() => onConstraintSelect(option.value)}
                    data-testid={`constraint-option-${option.value}`}
                  >
                    <span className="text-sm font-light text-foreground">{option.label}</span>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
